import { api } from '../../api/client';
import type { Role, SessionRow } from '../auth/api';

export interface AdminUser {
  id: string;
  email: string;
  display_name: string;
  role: Role;
  locale: string;
  is_active: boolean;
  mfa_required: boolean;
  archived_at: string | null;
  last_login_at: string | null;
  locked_until: string | null;
  failed_logins: number;
  totp_enrolled: boolean;
  passkey_count: number;
  active_sessions: number;
}

export interface AuditEntry {
  id: number;
  at: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  actor_user_id: string | null;
  actor_name: string | null;
  ip: string | null;
  user_agent: string | null;
  request_id: string | null;
  changes: Record<string, { old: unknown; new: unknown }> | null;
  context: Record<string, unknown> | null;
}

export interface AuditPage {
  entries: AuditEntry[];
  total: number;
}

export const listUsers = (includeArchived = false) =>
  api.get<AdminUser[]>(`/users?include_archived=${includeArchived}`);

export const createUser = (body: {
  email: string;
  display_name: string;
  password: string;
  role: Role;
  locale: string;
  mfa_required: boolean;
}) => api.post<AdminUser>('/users', body);

export const updateUser = (
  id: string,
  body: Partial<{
    display_name: string;
    role: Role;
    locale: string;
    is_active: boolean;
    mfa_required: boolean;
  }>,
) => api.patch<AdminUser>(`/users/${id}`, body);

export const resetPassword = (id: string, newPassword: string) =>
  api.post<void>(`/users/${id}/password`, { new_password: newPassword });

export const clearMfa = (id: string) => api.post<void>(`/users/${id}/clear-mfa`);

export const unlockUser = (id: string) => api.post<void>(`/users/${id}/unlock`);

export const archiveUser = (id: string) => api.delete<AdminUser>(`/users/${id}`);

export const userSessions = (id: string) => api.get<SessionRow[]>(`/users/${id}/sessions`);

export const revokeUserSessions = (id: string) => api.delete<void>(`/users/${id}/sessions`);

export interface AuditFilters {
  action?: string;
  entity_type?: string;
  entity_id?: string;
  actor_user_id?: string;
  since?: string;
  until?: string;
  limit?: number;
  offset?: number;
}

export const fetchAudit = (filters: AuditFilters) => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  return api.get<AuditPage>(`/audit?${params.toString()}`);
};

export const auditActions = () => api.get<string[]>('/audit/actions');
